<template>
  <button
    :id="action.value"
    :ref="action.value"
    class="action-key"
    :class="{ active }"
    :disabled="disabled"
    :aria-describedby="action.value"
    :data-entity="`producer-editor-${action.value}`"
    :data-test="`panel-editor-${hotkey.toLowerCase() || 'unmapped'}`"
    @click="handleClickAction($event, action)"
  >
    <div class="action-key-header" style="display:flex; flex-direction: column;" :id="action.value">
      <strong class="action-key-letter">{{hotkey}}</strong>
      <strong class="action-key-name">{{action.display}}</strong>
    </div>

    <transition-group>
      <span
        v-for="(val, i) in filteredRipples"
        :key="'ripple' + i"
        class="ripple"
        :ref="'ripple-' + i"
        :style="{'top': val.y + 'px', 'left': val.x + 'px'}"
        @animationend="rippleEnd(i)"
      />
    </transition-group>
  </button>
</template>

<script>
export default {
  props: {
    action: {
      type: Object,
      required: true,
    },

    production_context: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    /**
     * Highlights the button as the current/next relevant action.
     * Optional so panel-editor hotkeys are unaffected.
     */
    active: {
      type: Boolean,
      default: false,
    },

    /**
     * Disables the button (and its hotkey).
     * Optional so panel-editor hotkeys are unaffected.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [],

  inject: {
    onWidgetAction: {
      default: () => () => {},
    },
  },

  data() {
    return {
      ripples: [],
      hotkeyBound: false,
    };
  },

  mounted() {
    // Keep-alive deactivates (not unmounts) cached components, so bind/remove
    // the hotkey in both lifecycle paths to guarantee the listener is never
    // active for a hidden/deactivated panel or left registered after removal.
    this.bindHotkey();
  },

  activated() {
    this.bindHotkey();
  },

  deactivated() {
    this.unbindHotkey();
  },

  unmounted() {
    this.unbindHotkey();
  },

  computed: {
    filteredRipples() {
      return this.ripples.filter((r) => r.show);
    },

    hotkey() {
      return this.action.hotkey || '';
    },
  },

  methods: {
    bindHotkey() {
      if (this.hotkey && !this.hotkeyBound) {
        window.addEventListener('keypress', this.handleKeyPress);
        this.hotkeyBound = true;
      }
    },

    unbindHotkey() {
      if (this.hotkeyBound) {
        window.removeEventListener('keypress', this.handleKeyPress);
        this.hotkeyBound = false;
      }
    },

    rippleEnd(i) {
      this.ripples[i].show = false;
    },

    handleClickAction(e, action) {
      if (this.disabled) {
        return;
      }

      const el = this.$refs[action.value];
      if (!el) {
        console.warn('Ignoring handleClickAction for button that no longer exists:', action.value, this.$refs);
        return;
      }

      const pos = el.getBoundingClientRect();

      this.ripples.push({
        x: e.clientX - pos.left,
        y: e.clientY - pos.top,
        show: true,
      });

      switch (action.type) {
        case 'frame-trigger': {
          this.$emit('trigger', action.value);
          break;
        }

        case 'timer widget': {
          this.onWidgetAction(action);
          break;
        }

        default: {
          if (this.production_context.live) {
            this.$store.dispatch('sendAction', action);
          }

          if (this.production_context.preview) {
            this.$store.dispatch('sendPreviewAction', action);
          }
        }
      }
    },

    isEditableTarget(target) {
      const node = target?.nodeName?.toLowerCase();
      return node === 'input'
        || node === 'textarea'
        || node === 'select'
        || target?.isContentEditable;
    },

    handleKeyPress(e) {
      // Never fire the hotkey while the user is typing in a form control
      // (e.g. the timer's duration selects or date picker input).
      if (
        !this.disabled
        && !this.isEditableTarget(e.target)
        && this.hotkey.toLowerCase() === e.key.toLowerCase()
      ) {
        this.handleClickAction(e, this.action);
      }
    },
  },
};
</script>
